import { DateTime } from 'luxon';
import { TimeEntry } from 'services/tasks/types';
import { formatShortDueDate } from 'utils/date';
import { getExplicitUserTimeZone } from 'utils/timezone';

const MAX_MINUTES = 100000;

const timeEntryLabelValue = (entry: {
  createdAt?: string;
  endTime: string;
  minutes?: number;
  startTime?: string;
}) => (
  !entry.startTime && typeof entry.minutes !== 'number' && entry.createdAt
    ? entry.createdAt
    : entry.endTime
);

const formatEntryDate = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return formatShortDueDate(new Date(year, month - 1, day));
  }
  const parsed = DateTime.fromISO(value);
  if (!parsed.isValid) {
    return value;
  }
  const zone = getExplicitUserTimeZone();
  const dateTime = zone ? parsed.setZone(zone) : parsed;
  return `${formatShortDueDate(dateTime.toJSDate())} ${dateTime.toFormat('HH:mm')}`;
};

const formatLoggedHours = (minutes: number) => {
  const hours = Math.round((minutes / 60) * 100) / 100;
  return `${hours} h`;
};

const hoursInputToMinutes = (value: string, options?: { allowZero?: boolean }) => {
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours < 0 || (hours === 0 && !options?.allowZero)) {
    return null;
  }
  const minutes = Math.round(hours * 60);
  if (minutes < 0 || (minutes === 0 && !options?.allowZero) || minutes > MAX_MINUTES) {
    return null;
  }
  return minutes;
};

const minutesToHoursInput = (minutes: number) => (
  String(Math.round((minutes / 60) * 100) / 100)
);

const entryForSave = (entry: TimeEntry & {
  date?: string;
  endedAt?: string;
  startedAt?: string;
}): TimeEntry => {
  const {
    date,
    endedAt,
    startedAt,
    ...rest
  } = entry;
  const endTime = rest.endTime || endedAt || date;
  const startTime = rest.startTime || startedAt;
  return {
    ...rest,
    ...(endTime ? { endTime } : {}),
    ...(startTime ? { startTime } : {}),
  };
};

const minutesBetween = (start: Date, end: Date) => {
  const minutes = Math.round(
    DateTime.fromJSDate(end).diff(DateTime.fromJSDate(start), 'minutes').minutes,
  );
  if (!Number.isFinite(minutes) || minutes <= 0 || minutes > MAX_MINUTES) {
    return null;
  }
  return minutes;
};

const totalMinutes = (entries: TimeEntry[]) => (
  entries.reduce((sum, entry) => sum + (entry.minutes ?? 0), 0)
);

const createdAtMillis = (value: Date | string | undefined) => {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value !== 'string') {
    return 0;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortTimeEntries = <T extends { createdAt?: Date | string; endTime: string }>(entries: T[]) => (
  entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => (
      b.entry.endTime.localeCompare(a.entry.endTime)
      || createdAtMillis(b.entry.createdAt) - createdAtMillis(a.entry.createdAt)
      || b.index - a.index
    ))
    .map(({ entry }) => entry)
);

const latestRemainingTime = (
  entries: Array<{ endTime: string; estimatedRemainingTime?: number | null }> | undefined,
): number | null | undefined => {
  const latest = sortTimeEntries(entries ?? []).find(entry => entry.estimatedRemainingTime !== undefined);
  return latest ? latest.estimatedRemainingTime : undefined;
};

const currentRemainingTime = (
  entries: Array<{ endTime: string; estimatedRemainingTime?: number | null }> | undefined,
  legacyRemainingTime?: number | null,
): number | null | undefined => {
  const fromEntries = latestRemainingTime(entries);
  return fromEntries !== undefined ? fromEntries : legacyRemainingTime;
};

const taskEstimatedRemainingMinutes = (task?: {
  estimatedCompletionTime?: number | null;
  remainingTime?: number | null;
  timeEntries?: Array<{ endTime: string; estimatedRemainingTime?: number | null }>;
} | null): number | null | undefined => {
  if (!task) {
    return undefined;
  }
  const fromLog = currentRemainingTime(task.timeEntries, task.remainingTime);
  if (fromLog !== undefined) {
    return fromLog;
  }
  return task.estimatedCompletionTime;
};

const completionDurationMinutes = (task?: {
  estimatedCompletionTime?: number | null;
  remainingTime?: number | null;
  timeEntries?: Array<{ endTime: string; estimatedRemainingTime?: number | null }>;
} | null): number | undefined => {
  const remaining = taskEstimatedRemainingMinutes(task);
  if (typeof remaining === 'number' && remaining > 0) {
    return remaining;
  }
  return undefined;
};

const suggestedRemainingMinutes = (
  currentRemaining: number | null | undefined,
  loggedMinutes: number | null,
): number | null | undefined => {
  if (typeof currentRemaining !== 'number') {
    return currentRemaining;
  }
  if (loggedMinutes == null) {
    return currentRemaining;
  }
  return Math.max(0, currentRemaining - loggedMinutes);
};

export {
  completionDurationMinutes,
  currentRemainingTime,
  entryForSave,
  formatEntryDate,
  formatLoggedHours,
  taskEstimatedRemainingMinutes,
  hoursInputToMinutes,
  latestRemainingTime,
  minutesBetween,
  minutesToHoursInput,
  sortTimeEntries,
  suggestedRemainingMinutes,
  timeEntryLabelValue,
  totalMinutes,
};
